<template>
  <div>
    <h1>
      {{ t('createDigitalizationProject.addDigitalizationProject') }}
    </h1>
    <p>{{ t('createDigitalizationProject.requiredFieldsMarkedWith') }}</p>
    <project-form
      :project-type="ProjectType.DIGITALIZATION_PROJECT"
      @submit="handleSubmit"
    >
      <docu-responsibles-table
        :mode="DocuResponsiblesTableMode.NEW_PROJECT"
        @update:selected-docu-responsibles="handleUpdateDocuResponsiblesEvent"
      />
      <message-wrapper
        v-if="isMissingDocuResponsibleError"
        class="no-docu-responsible-assigned-error-message"
        content-centred
        severity="error"
      >
        {{
          t('createDigitalizationProject.errors.noDocuResponsibleAdded.long')
        }}
      </message-wrapper>
    </project-form>
  </div>
</template>

<script lang="ts" setup>
  import { ref } from 'vue';
  import { useRouter } from 'vue-router';
  import { useToast } from '@/composables/useToast';
  import { useI18n } from 'vue-i18n';
  import { IDocuResponsible, ProjectType } from 'types';
  import { ProjectFormFields } from '@/components/ProjectForm/ProjectForm.types';
  import { isAxiosError } from 'axios';
  import { DocuResponsiblesTableMode } from '@/components/DocuResponsiblesTable/DocuResponsiblesTable.types';
  import { Route, routeParams } from '@/routes';
  import { ProjectDataService } from '@/services';
  import { computed } from 'vue';

  const addedDocuResponsibles = ref<string[]>([]);
  const isCreatingDigitalizationProject = ref(false);
  const isSubmitted = ref<boolean>(false);

  const isMissingDocuResponsibleError = computed(() => {
    return isSubmitted.value && !addedDocuResponsibles.value.length;
  });

  const router = useRouter();
  const { createErrorToast, createSuccessToast } = useToast();
  const { t } = useI18n();

  async function handleSubmit(
    digitalizationProjectData: ProjectFormFields,
  ): Promise<void> {
    isSubmitted.value = true;
    if (!addedDocuResponsibles.value.length) return;

    isCreatingDigitalizationProject.value = true;
    try {
      const projectId = await ProjectDataService.createDigitalizationProject(
        digitalizationProjectData,
        addedDocuResponsibles.value,
      );
      router.push({
        name: Route.DIGITALIZATION_PROJECT_DETAILS,
        params: {
          [routeParams[Route.DIGITALIZATION_PROJECT_DETAILS].projectId]:
            projectId,
        },
      });
      createSuccessToast(t('createDigitalizationProject.projectCreated'));
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 409) {
        createErrorToast(
          t(
            'createDigitalizationProject.errors.digitalizationProjectNumberNotUnique.long',
          ),
        );
      } else {
        createErrorToast(
          t(
            'createDigitalizationProject.errors.errorCreatingDigitalizationProject.long',
          ),
        );
      }
    } finally {
      isCreatingDigitalizationProject.value = false;
    }
  }

  function handleUpdateDocuResponsiblesEvent(
    docuResponsibles: IDocuResponsible[],
  ): void {
    addedDocuResponsibles.value = docuResponsibles.map(
      (docuResponsible) => docuResponsible.oid,
    );
  }
</script>

<style lang="scss" scoped>
  .no-docu-responsible-assigned-error-message {
    margin-top: 1rem;
    text-align: center;
  }
</style>
